import {createTheme} from "@mui/material";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary:    { main: "#e85878", light: "#f07090", dark: "#c43058" },
    secondary:  { main: "#7c6af7" },
    success:    { main: "#34d399" },
    warning:    { main: "#fbbf24" },
    info:       { main: "#22d3ee" },
    background: { default: "#070812", paper: "#111328" },
    text:       { primary: "#f1f5f9", secondary: "#8892b0" },
    divider:    "rgba(255,255,255,0.07)",
    action: {
      hover:    "rgba(255,255,255,0.05)",
      selected: "rgba(124,106,247,0.12)",
    },
  },
  typography: {
    fontFamily: '"covesLight","Inter",system-ui,sans-serif',
    fontSize: 13,
    h6:      { fontWeight: 700, letterSpacing: "-0.01em" },
    subtitle2:{ color: "#8892b0", fontSize: "11px", fontWeight: 600,
                textTransform: "uppercase", letterSpacing: "0.07em" },
    caption: { color: "#4a5268", fontSize: "11px" },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: "#070812", backgroundImage: "none" },
      },
    },

    /* ── Paper / surfaces ── */
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: "#111328",
          border: "1px solid rgba(255,255,255,0.07)",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "#111328",
          border: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
        },
      },
    },

    /* ── Buttons ── */
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, borderRadius: "8px", letterSpacing: "0.01em" },
        contained: {
          background: "linear-gradient(135deg,#e85878,#c43058)",
          color: "#fff",
          boxShadow: "0 4px 16px rgba(232,88,120,0.3)",
          "&:hover": {
            background: "linear-gradient(135deg,#f07090,#e85878)",
            boxShadow: "0 6px 20px rgba(232,88,120,0.4)",
          },
        },
        outlined: {
          borderColor: "rgba(255,255,255,0.12)",
          color: "#c8d0e0",
          "&:hover": { borderColor: "rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.04)" },
        },
        text: {
          color: "#8892b0",
          "&:hover": { background: "rgba(255,255,255,0.04)", color: "#f1f5f9" },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#8892b0",
          "&:hover": { background: "rgba(255,255,255,0.06)", color: "#f1f5f9" },
        },
      },
    },

    /* ── Inputs ── */
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: "#0f1024",
            "& fieldset":                      { borderColor: "rgba(255,255,255,0.1)" },
            "&:hover fieldset":                { borderColor: "rgba(255,255,255,0.2)" },
            "&.Mui-focused fieldset":          { borderColor: "#7c6af7" },
          },
          "& .MuiInputLabel-root":             { color: "#8892b0", fontSize: "13px" },
          "& .MuiInputLabel-root.Mui-focused": { color: "#7c6af7" },
          "& input":                           { color: "#f1f5f9", fontSize: "13px" },
        },
      },
    },

    /* ── Table ── */
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: "1px solid rgba(255,255,255,0.05)", color: "#f1f5f9", padding: "10px 12px" },
        head: { color: "#4a5268", fontWeight: 700, fontSize: "10px",
                textTransform: "uppercase", letterSpacing: "0.08em", padding: "8px 12px" },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover td": { backgroundColor: "rgba(124,106,247,0.04)" },
          transition: "background 0.12s",
        },
      },
    },

    /* ── Menu ── */
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "13px", color: "#c8d0e0", gap: "10px", minHeight: "36px",
          "&:hover": { backgroundColor: "rgba(124,106,247,0.08)" },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "#171a38",
          border: "1px solid rgba(255,255,255,0.09)",
          boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
          minWidth: 180,
        },
      },
    },

    /* ── Misc ── */
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: "rgba(255,255,255,0.07)", borderRadius: 4 },
        bar:  { background: "linear-gradient(90deg,#e85878,#7c6af7)", borderRadius: 4 },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: { color: "#2a2d48" },
        ol:        { flexWrap: "nowrap", alignItems: "center" },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#1c1f3a",
          border: "1px solid rgba(255,255,255,0.09)",
          fontSize: "11px", color: "#e2e8f0", borderRadius: "6px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        },
        arrow: { color: "#1c1f3a" },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: "rgba(255,255,255,0.07)" } },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255,255,255,0.06)",
          color: "#8892b0",
          border: "1px solid rgba(255,255,255,0.08)",
          fontSize: "11px", height: "26px",
          "&:hover": { backgroundColor: "rgba(124,106,247,0.12)", color: "#f1f5f9" },
        },
        colorPrimary: {
          backgroundColor: "rgba(232,88,120,0.12)",
          color: "#e85878",
          border: "1px solid rgba(232,88,120,0.25)",
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: "8px", fontSize: "13px" } },
    },
  },
});

export default theme;
